import { type ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DashboardRounded from '@mui/icons-material/DashboardRounded';
import VideocamRounded from '@mui/icons-material/VideocamRounded';
import LibraryMusicRounded from '@mui/icons-material/LibraryMusicRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import { FullscreenButton } from '../../../../kits/dashboard/lib/FullscreenButton';
import { SidebarShell } from '../../../../kits/dashboard/lib/SidebarShell';

declare module '@mui/material/styles' {
  interface Theme {
    customBackgrounds: { app: string; card: string; overlay: string };
  }
  interface ThemeOptions {
    customBackgrounds?: { app: string; card: string; overlay: string };
  }
}
const NAVIGATION = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardRounded /> },
  { label: 'Cam', path: '/cam', icon: <VideocamRounded /> },
  { label: 'Songs', path: '/songs', icon: <LibraryMusicRounded /> },
  { label: 'Learn', path: '/learn', icon: <SchoolRounded /> },
  { label: 'Agent', path: '/agent', icon: <AutoAwesomeRounded /> },
  { label: 'Settings', path: '/settings', icon: <TuneRounded /> },
];

export interface JamcamLayoutProps {
  children: ReactNode;
  immersive?: boolean;
  authenticated: boolean;
  authLoading?: boolean;
  displayName?: string;
  onSignIn: () => void | Promise<unknown>;
  onSignUp?: () => void | Promise<unknown>;
  onSignOut: () => void | Promise<unknown>;
}

export function JamcamLayout({
  children,
  immersive = false,
  authenticated,
  authLoading = false,
  displayName = 'Your account',
  onSignIn,
  onSignUp,
  onSignOut,
}: JamcamLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [touring, setTouring] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  useEffect(() => {
    setOpen(false);
    setTouring(false);
  }, [location.pathname]);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (
        event as CustomEvent<{ open: boolean; touring?: boolean }>
      ).detail;
      setOpen(detail.open);
      setTouring(detail.touring ?? false);
    };
    window.addEventListener('jamcam:navigation', listener);
    return () => window.removeEventListener('jamcam:navigation', listener);
  }, []);
  const login = async () => {
    setAuthError(null);
    try {
      if (location.pathname === '/demo' && onSignUp) await onSignUp();
      else await onSignIn();
    } catch {
      setAuthError('Could not open sign in. Please try again.');
    }
  };
  const logout = async () => {
    try {
      await onSignOut();
      setOpen(false);
      navigate('/');
    } catch {
      setAuthError('Could not sign out. Please try again.');
    }
  };
  const itemStyle = {
    borderRadius: '8px',
    mb: 0.5,
    minHeight: 46,
    '&.Mui-selected': { bgcolor: 'action.selected', color: 'primary.light' },
    '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
  };

  return (
    <SidebarShell
      immersive={immersive}
      overlayActions={
        immersive ? (
          <FullscreenButton
            data-tour-id="stage-fullscreen"
            sx={{ width: 40, height: 40, borderRadius: '8px' }}
          />
        ) : undefined
      }
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      modalNavigation={!touring}
      navigationLabel="JamCam navigation"
      brand={
        <Box
          component={RouterLink}
          to={authenticated ? '/dashboard' : '/demo'}
          data-tour-id="nav-logo"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Box
            component="img"
            src="/assets/branding/jc_logo-horizontal_transparent.png"
            alt="JamCam home"
            sx={{ width: 150, maxWidth: '100%', height: 'auto' }}
          />
        </Box>
      }
      navigation={
        <>
          <List data-tour-id="nav-pill" aria-label="Practice and tools">
            {NAVIGATION.map((item) => {
              const selectedPath = location.pathname === '/tabs' || location.pathname.startsWith(
                '/learn/tab-practice'
              )
                ? '/songs'
                : location.pathname;
              const selected =
                selectedPath === item.path ||
                selectedPath.startsWith(`${item.path}/`);
              return (
                <ListItemButton
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  disabled={!authenticated && !authLoading}
                  selected={selected}
                  aria-current={selected ? 'page' : undefined}
                  data-tour-id={`nav-link-${item.path.slice(1)}`}
                  onClick={() => setOpen(false)}
                  sx={itemStyle}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </List>
          <Divider sx={{ my: 2 }} />
          <List aria-label="Help and display">
            <ListItemButton
              data-tour-id="nav-help"
              sx={itemStyle}
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(
                  new CustomEvent('jamcam:open-tour', {
                    detail: { tour: 'overview' },
                  })
                );
              }}
            >
              <ListItemIcon>
                <HelpOutlineRounded />
              </ListItemIcon>
              <ListItemText primary="Getting started" />
            </ListItemButton>
          </List>
          {immersive && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pt: 2 }}
            >
              Close this menu to return to your stage.
            </Typography>
          )}
        </>
      }
      footer={
        <Stack spacing={1}>
          {authenticated ? (
            <>
              <Button
                component={RouterLink}
                to="/account"
                onClick={() => setOpen(false)}
                data-tour-id="nav-account"
                startIcon={<PersonRounded />}
                color="inherit"
                sx={{ justifyContent: 'flex-start' }}
              >
                <Box
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </Box>
              </Button>
              <Button
                onClick={logout}
                startIcon={<LogoutRounded />}
                color="inherit"
                sx={{ justifyContent: 'flex-start', color: 'text.secondary' }}
              >
                Sign out
              </Button>
            </>
          ) : (
            <Button
              data-tour-id="nav-account"
              variant="contained"
              onClick={login}
              disabled={authLoading}
            >
              {location.pathname === '/demo' ? 'Sign up free' : 'Sign in'}
            </Button>
          )}
          {authError && (
            <Typography role="alert" variant="caption" color="error.main">
              {authError}
            </Typography>
          )}
          <Stack direction="row" spacing={2} sx={{ px: 1, pt: 1 }}>
            <Link
              component={RouterLink}
              to="/privacy"
              onClick={() => setOpen(false)}
              variant="caption"
              color="text.secondary"
            >
              Privacy
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              onClick={() => setOpen(false)}
              variant="caption"
              color="text.secondary"
            >
              Terms
            </Link>
          </Stack>
        </Stack>
      }
    >
      {/* Same element type in both modes so route changes don't remount the app subtree. */}
      <Container
        maxWidth={immersive ? false : 'lg'}
        disableGutters={immersive}
        sx={immersive ? { height: '100%' } : { py: { xs: 2, md: 4 } }}
      >
        {children}
      </Container>
    </SidebarShell>
  );
}
export default JamcamLayout;
